import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { loginApi, logoutApi, updatePasswordApi } from '../api/authApi';
import { getDefaultRouteForRole, normalizeRoles } from '@/lib/utils/roles';
import type { UserRole } from '@/types/entities';

export function useAuth() {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    isLoading,
    setUser,
    setToken,
    setLoading,
    logout: clearAuth,
    hasRole,
    getUserRoles,
  } = useAuthStore();

  const login = useCallback(
    async (username: string, password: string) => {
      setLoading(true);
      try {
        const response = await loginApi(username, password);
        const { userId, jwt, roles } = response;

        const authUser = {
          username: userId,
          role: roles,
        };

        setToken(jwt);
        setUser(authUser);
        
        localStorage.setItem('refreshToken', jwt);
        sessionStorage.setItem('user', JSON.stringify(authUser));

        const userRoles = normalizeRoles(roles);
        const redirectPath = getDefaultRouteForRole(userRoles);
        navigate(redirectPath);
      } catch (error: unknown) {
        const message = axios.isAxiosError<{ message?: string }>(error)
          ? error.response?.data?.message || error.message || 'Login failed'
          : error instanceof Error
            ? error.message
            : 'Login failed';
        notifications.show({
          title: 'Error',
          message,
          color: 'red',
          autoClose: 5000,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [navigate, setLoading, setToken, setUser]
  );

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await logoutApi();
      clearAuth();
      
      const userRoles = getUserRoles();
      if (userRoles.includes('STUDENT')) {
        navigate('/exam');
      } else {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [clearAuth, getUserRoles, navigate, setLoading]);

  const updatePassword = useCallback(
    async (
      userId: string,
      oldPassword: string,
      newPassword: string,
      confirmPassword: string
    ) => {
      setLoading(true);
      try {
        await updatePasswordApi(userId, oldPassword, newPassword, confirmPassword);
        notifications.show({
          title: 'Success',
          message: 'Password updated successfully',
          color: 'green',
          autoClose: 5000,
        });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to update password';
        notifications.show({
          title: 'Error',
          message,
          color: 'red',
          autoClose: 5000,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const canAccess = useCallback(
    (requiredRole: UserRole): boolean => {
      if (!isAuthenticated || !user) return false;
      const userRoles = getUserRoles();
      if (userRoles.includes('ADMIN')) return true;
      return userRoles.includes(requiredRole);
    },
    [getUserRoles, isAuthenticated, user]
  );

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    updatePassword,
    hasRole,
    canAccess,
    getUserRoles,
  };
}
